import { useMemo } from 'react';
import { Modal } from '../../components/Modal.tsx';
import { Loading, ErrorState } from '../../components/ui.tsx';
import { EnginePreview } from '../../components/EnginePreview.tsx';
import { buildPlaylistManifest } from '../../lib/preview-manifest.ts';
import { usePlaylist } from '../../api/playlists.ts';
import { useMedia } from '../../api/media.ts';
import type { Media } from '@seencast/shared';

/** True-preview of a saved playlist's loop, mounted in a modal from the playlists list.
 * Loads the playlist's ordered items + media and plays them through the real engine. */
export function PlaylistPreviewModal({
  playlistId,
  name,
  onClose,
}: {
  playlistId: string;
  name: string;
  onClose: () => void;
}) {
  const playlist = usePlaylist(playlistId);
  const media = useMedia();

  const mediaById = useMemo(() => {
    const map = new Map<string, Media>();
    (media.data ?? []).forEach((m) => map.set(m.id, m));
    return map;
  }, [media.data]);

  const manifest = useMemo(() => {
    if (!playlist.data) return null;
    const rows = playlist.data.items.map((it) => ({
      key: it.id,
      media_id: it.media_id,
      duration_ms: it.duration_ms,
      fit: it.fit,
    }));
    return buildPlaylistManifest(rows, mediaById);
  }, [playlist.data, mediaById]);

  const loading = playlist.isLoading || media.isLoading;

  return (
    <Modal title={`Preview · ${name}`} onClose={onClose} size="lg">
      {loading ? (
        <Loading />
      ) : playlist.isError || !playlist.data ? (
        <ErrorState message="Could not load this playlist." onRetry={() => playlist.refetch()} />
      ) : (
        <EnginePreview
          manifest={manifest}
          emptyLabel="This playlist is empty — add media to preview it."
          ariaLabel={`Preview of ${name}`}
        />
      )}
    </Modal>
  );
}
